import React, { useEffect, useMemo, useState } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import HelpOutlineIcon from '@mui/icons-material/HelpOutlineRounded';
import RemoveIcon from '@mui/icons-material/RemoveRounded';
import ListItemText from '@mui/material/ListItemText';
import { makeStyles, withStyles, withTheme } from '@mui/styles';
import styles from './Sidebar.styles';
import { Resizable } from 're-resizable';
import services from 'services';
import Progress from 'studio/components/Progress';
import Link from 'studio/components/Link';
import MarkdownHtml from 'studio/components/MarkdownHtml/MarkdownHtml';

const Documentation = ({ keyword }) => {
  const useStyles = makeStyles(theme => ({
    title: {
      marginBottom: theme.spacing(2),
      fontSize: 17,
      fontWeight: 500
    },
    link: {
      marginTop: theme.spacing(3)
    }
  }));
  const classes = useStyles();

  // contains the documentations of and indexed by keywords:
  //   - if the property doesn't exist: documentation for the keyword hasn't been retrieved yet
  //   - if the property exists and is 'undefined': unknown keyword
  //   - if the property exists and is 'null': no documentation exists for the keyword
  //   - else if the property exists it contains the documentation object
  const [documentations, setDocumentations] = useState({});

  // retrieves the documentation whenever the keyword changes
  useEffect(() => {
    // check if the documentation for the current keyword has already been retrieved
    if (keyword && !documentations.hasOwnProperty(keyword)) {
      (async () => {
        // retrieve the documentation
        let response = await services.getDocumentation(keyword);
        const newDocumentations = { ...documentations };
        if (response.status === 200) {
          // the documentation for the keyword exists
          newDocumentations[keyword] = response.data.data;
        } else {
          response = response.response;
          if (response.status === 400) {
            // the keyword is unknown
            newDocumentations[keyword] = undefined;
          } else {
            // no documentation is available for the keyword
            newDocumentations[keyword] = null;
          }
        }
        setDocumentations(newDocumentations);
      })();
    }
  }, [keyword, documentations]);

  // specifies if the documentation for the keyword is loading
  const documentationIsLoading = useMemo(
    () => !!keyword && !documentations.hasOwnProperty(keyword),
    [keyword, documentations]
  );
  // the documentation for the keyword
  const documentation = useMemo(() => documentations[keyword], [keyword, documentations]);

  const title = (
    <Typography variant="subtitle1" className={classes.title}>
      {keyword}
    </Typography>
  );

  return (
    <div>
      {documentationIsLoading ? (
        <>
          {title}
          <Progress />
        </>
      ) : (
        <>
          {!keyword || documentation === undefined ? (
            <p>Move the cursor on a keyword to see its documentation</p>
          ) : (
            <div data-test="editor-sidebar-documentation">
              {title}
              {documentation ? (
                <>
                  <MarkdownHtml html={documentation?.html} />
                  {documentation?.url && (
                    <div className={classes.link}>
                      <Tooltip title="Open the documentation in a new window">
                        <Link href={documentation.url} newWindow>
                          Go to documentation
                        </Link>
                      </Tooltip>
                    </div>
                  )}
                </>
              ) : (
                <>No documentation available</>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const localStorageKeys = {
  activeSectionIndex: 'veezoo.studio.editor.sidebar.activeSectionIndex',
  width: 'veezoo.studio.editor.sidebar.width'
};

const Sidebar = ({ classes, onResize, activeKeyword }) => {
  // the sections to display in the sidebar
  const sections = useMemo(
    () => [
      {
        icon: <HelpOutlineIcon />,
        title: 'Documentation',
        content: <Documentation keyword={activeKeyword} />
      }
    ],
    [activeKeyword]
  );

  // the index of the section currently being displayed
  const [activeSectionIndex, setActiveSectionIndex] = useState(null);
  // the section currently being displayed
  const activeSection = useMemo(() => sections[activeSectionIndex], [sections, activeSectionIndex]);

  // calls the 'onResize' listener whenever the active section changes
  useEffect(() => {
    onResize?.();
  }, [activeSection, onResize]);

  // the width of the sidebar: use the value from local storage, else fall back to a default
  const width = localStorage.getItem(localStorageKeys.width) || 300;

  // opens the section upon initialization
  useEffect(() => {
    // get the index of the active section from local storage (if any)
    const localStorageActiveSectionIndex = localStorage.getItem(localStorageKeys.activeSectionIndex);
    // if the local storage contains no value...
    if (localStorageActiveSectionIndex === null) {
      // ...open the first section
      openSection(0);
    } else if (localStorageActiveSectionIndex >= 0 && localStorageActiveSectionIndex < sections.length) {
      // ...else open the section given the index (if valid)
      openSection(localStorageActiveSectionIndex);
    }
  }, []);

  const openSection = index => {
    setActiveSectionIndex(index);
    // store the section's index in the local storage
    localStorage.setItem(localStorageKeys.activeSectionIndex, index);
  };

  const closeSidebar = () => {
    setActiveSectionIndex(null);
    // store an invalid index in the local storage
    localStorage.setItem(localStorageKeys.activeSectionIndex, -1);
  };

  const toTestAttribute = section => section.title.toLowerCase().replaceAll(' ', '-');

  return (
    <div className={classes.root}>
      {!!activeSection && (
        <>
          <Resizable
            className={classes.container}
            defaultSize={{ width }}
            minWidth={200}
            enable={{
              top: false,
              right: false,
              bottom: false,
              left: true,
              topRight: false,
              bottomRight: false,
              bottomLeft: false,
              topLeft: false
            }}
            onResize={_ => onResize()}
            onResizeStop={(event, direction, element) => {
              // store the width in the local storage
              localStorage.setItem(localStorageKeys.width, element.offsetWidth);
            }}
          >
            <List disablePadding>
              <ListItem selected component="div" className={classes.sectionTitle}>
                <ListItemIcon>{activeSection.icon}</ListItemIcon>
                <ListItemText primary={activeSection.title} />
                <ListItemSecondaryAction>
                  <Tooltip title={`Close ${activeSection.title}`}>
                    <IconButton size="small" onClick={closeSidebar} data-test="editor-sidebar-close">
                      <RemoveIcon />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
            <Divider />
            <div
              className={classes.sectionContent}
              data-test={`editor-sidebar-section-${toTestAttribute(activeSection)}`}
            >
              {activeSection.content}
            </div>
          </Resizable>
        </>
      )}

      {!activeSection && (
        <div className={classes.sections}>
          {sections.map((section, index) => {
            const isSelected = index === activeSectionIndex;
            return (
              <Tooltip key={index} title={`Open ${section.title}`}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={isSelected}
                    onClick={_ => (isSelected ? closeSidebar() : openSection(index))}
                    data-test={`editor-sidebar-button-${toTestAttribute(section)}`}
                  >
                    <ListItemIcon>{section.icon}</ListItemIcon>
                    <ListItemText primary={section.title} />
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default withTheme(withStyles(styles)(Sidebar));
